import { Card, CardContent } from "@shared/components/ui/card";
import {
  Phone,
  MapPin,
  Mail,
  UserCircle,
  UserCheck,
  Calendar,
} from "lucide-react";
import { USER_ROLE_COLORS } from "@shared/utils/constants";
import { Link } from "react-router-dom";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@shared/components/ui/avatar";
import UserActions from "./users-actions";
import { format } from "date-fns";
import { Badge } from "@shared/components/ui/badge";
import { useTranslation } from "react-i18next";
import { getFileUrl } from "@shared/utils/fileUrl";

const UserCard = ({ user }) => {
  const { t } = useTranslation();
  const isActive = user.isActive;

  const getRoleBadgeVariant = (role) => {
    const variants = {
      admin: "destructive",
      manager: "default",
      client: "secondary",
      livreur: "outline",
      collector: "secondary",
      clientSupport: "secondary",
      accountant: "secondary",
    };
    return variants[role] || "secondary";
  };

  return (
    <Card
      className={`${
        isActive ? `border-l-4 ${USER_ROLE_COLORS[user.role]}` : "opacity-70"
      } w-full shadow-sm hover:shadow-md transition-all duration-200`}
    >
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* User Info Section */}
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <Link to={`/users/${user._id}`}>
              <Avatar className="h-16 w-16 border-2 border-background ring-2 ring-muted">
                <AvatarImage
                  src={getFileUrl(user.profileImage)}
                  alt={`${user.fullName} profile`}
                  className="object-cover"
                />
                <AvatarFallback className="text-lg font-semibold bg-gradient-to-br from-primary/20 to-primary/10">
                  {user.brandName?.substring(0, 2).toUpperCase() || "US"}
                </AvatarFallback>
              </Avatar>
            </Link>

            <div className="flex-1 min-w-0 space-y-3">
              {/* Header with Name and Role */}
              <div className="flex items-start justify-between gap-2">
                <Link
                  to={`/users/${user._id}`}
                  className="flex-1 min-w-0 hover:opacity-80 transition-opacity"
                >
                  <h3 className="font-semibold text-lg truncate">
                    {user.fullName}
                  </h3>
                  <p className="text-sm text-muted-foreground truncate">
                    {user.brandName}
                  </p>
                </Link>
                <Badge
                  variant={getRoleBadgeVariant(user.role)}
                  className="shrink-0"
                >
                  {t(`role.${user.role}`)}
                </Badge>
              </div>

              {/* Contact and Location Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="truncate">{user.email}</span>
                </div>

                <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span className="truncate">{user.phone}</span>
                </div>

                <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {user.city || t("main.notSet")}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {format(new Date(user.createdAt), "dd/MM/yyyy")}
                  </span>
                </div>

                {user.collector && (
                  <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                    <UserCircle className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {user.collector.brandName || user.collector.fullName}
                    </span>
                  </div>
                )}

                {user.assignedSupport && (
                  <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                    <UserCheck className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {user.assignedSupport.brandName ||
                        user.assignedSupport.fullName}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions Section */}
          <div className="flex lg:flex-col items-center justify-end gap-2 lg:self-start">
            <UserActions user={user} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserCard;
